import { useEffect, useMemo, useRef, useState } from "react";
import { Button, CircularProgress } from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import Drawer from "../../../components/ui/Drawer";
import "../darkSurface.css";
import GoalValueControl from "../../results/components/GoalValueControl";
import { useScriptMode } from "../../../contexts/ScriptModeContext";
import { useToast } from "../../../components/ui/Toast";
import { useJoinHatimByCodeMutation, useLazyGetParticipationInfoQuery } from "../../../api/hatimApi";
import { availableJuzBlocks, sumPages } from "../../../lib/hatimEditing";
import { HatimIcon } from "../../../components/ui/mdiIcons";

const CODE_LENGTH = 8;
const CODE_CHAR = /^[A-Z0-9]$/;
const TOTAL_STEPS = 3;

/** 8 haneli kod için kutu kutu giriş; yapıştırma ve ok tuşları desteklenir. */
function CodeBoxes({ value, onChange, disabled }) {
  const refs = useRef([]);
  const chars = value.padEnd(CODE_LENGTH, " ").slice(0, CODE_LENGTH).split("");

  const setCharAt = (index, char) => {
    const next = chars.map((c, i) => (i === index ? char : c)).join("").trimEnd();
    onChange(next.replace(/\s/g, ""));
  };

  const handleChange = (index, raw) => {
    const char = (raw || "").toUpperCase().slice(-1);
    if (char && !CODE_CHAR.test(char)) return;
    setCharAt(index, char || " ");
    if (char && index < CODE_LENGTH - 1) refs.current[index + 1]?.focus();
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !chars[index].trim() && index > 0) {
      refs.current[index - 1]?.focus();
    }
    if (e.key === "ArrowLeft" && index > 0) refs.current[index - 1]?.focus();
    if (e.key === "ArrowRight" && index < CODE_LENGTH - 1) refs.current[index + 1]?.focus();
  };

  const handlePaste = (e) => {
    const text = (e.clipboardData.getData("text") || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
    if (!text) return;
    e.preventDefault();
    onChange(text.slice(0, CODE_LENGTH));
    refs.current[Math.min(text.length, CODE_LENGTH - 1)]?.focus();
  };

  return (
    <div className="flex items-center justify-between gap-1.5 sm:gap-2" onPaste={handlePaste}>
      {chars.map((char, index) => (
        <input
          // eslint-disable-next-line react/no-array-index-key
          key={index}
          ref={(el) => {
            refs.current[index] = el;
          }}
          data-code-box={index}
          value={char.trim()}
          disabled={disabled}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          inputMode="text"
          autoComplete="off"
          aria-label={`kod-${index + 1}`}
          className="w-full aspect-square max-w-[56px] rounded-xl border-2 border-terakkiBorder bg-terakkiSurfaceAlt
            text-center text-2xl font-Bold text-terakkiInk uppercase caret-terakkiPrimaryDark
            focus:outline-none focus:border-terakkiPrimaryDark focus:bg-terakkiSurface transition-colors"
        />
      ))}
    </div>
  );
}

function StepHeader({ step, title, description, t }) {
  const percent = Math.round(((step + 1) / TOTAL_STEPS) * 100);
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-xl font-SemiBold text-terakkiInk">{title}</h3>
        <span className="shrink-0 text-sm font-Bold px-2.5 py-1 rounded-full bg-terakkiBrandCloud text-terakkiPrimaryDark">
          {step + 1}/{TOTAL_STEPS} {t("join_step_label")}
        </span>
      </div>
      <div className="h-1.5 rounded-full bg-terakkiBrandMist overflow-hidden">
        <div
          data-testid="join-progress"
          className="h-full rounded-full bg-terakkiPrimaryDark transition-[width] duration-300"
          style={{ width: `${percent}%` }}
        />
      </div>
      <p className="text-base leading-relaxed text-terakkiNeutral">{description}</p>
    </div>
  );
}

function InfoLine({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-3 py-2.5 border-b border-terakkiBorder last:border-b-0">
      <span className="text-base text-terakkiNeutral shrink-0">{label}</span>
      <span className="text-base text-terakkiInk font-SemiBold text-right">{value}</span>
    </div>
  );
}

/**
 * Kodla hatime katılma akışı: kod → pay seçimi → onay.
 * Adım açıklamaları mobil uygulamadaki HatimJoinModal ile aynı.
 */
export default function JoinHatimDrawer({ open, onClose }) {
  const { t } = useScriptMode();
  const toast = useToast();

  const [step, setStep] = useState(0);
  const [code, setCode] = useState("");
  const [amount, setAmount] = useState(1);
  const [blocks, setBlocks] = useState([]);

  const codeValid = code.length === CODE_LENGTH;

  const [lookup, { data: lookupData, isFetching, isError }] = useLazyGetParticipationInfoQuery();
  const [joinByCode, { isLoading: joining }] = useJoinHatimByCodeMutation();
  const info = lookupData?.data;
  const isRange = Boolean(info?.isRangeType);

  useEffect(() => {
    if (!open) {
      setStep(0);
      setCode("");
      setAmount(1);
      setBlocks([]);
    }
  }, [open]);

  const availableBlocks = useMemo(
    () => (info && isRange ? availableJuzBlocks(info) : []),
    [info, isRange]
  );

  const toggleBlock = (block) => {
    setBlocks((prev) =>
      prev.some((b) => b.juz === block.juz)
        ? prev.filter((b) => b.juz !== block.juz)
        : [...prev, block]
    );
  };

  const shareValid = isRange ? blocks.length > 0 : Number(amount) >= 1;
  const maxCount = Number(info?.participation?.maxCount || 0);

  const handleLookup = async () => {
    const result = await lookup({ code });
    if (result.error || !result.data?.data) {
      toast.error(result.error?.message || t("hatim_join_code_lookup_error"));
      return;
    }
    setStep(1);
  };

  const handleJoin = async () => {
    const payload = isRange
      ? {
          code,
          pageRanges: blocks
            .slice()
            .sort((a, b) => a.startPage - b.startPage)
            .map((b) => ({ startPage: b.startPage, endPage: b.endPage })),
        }
      : { code, amount: Number(amount) };

    const result = await joinByCode(payload);
    if (result.error) {
      toast.error(result.error.message || t("hatim_join_code_error"));
      return;
    }
    toast.success(t("hatim_join_code_success"));
    onClose();
  };

  const stepTitle = [t("join_step_code_title"), t("join_step_share_title"), t("join_step_confirm_title")][step];
  const stepDesc =
    step === 0
      ? t("join_step_code_desc")
      : step === 1
      ? isRange
        ? t("join_step_share_desc_range")
        : t("join_step_share_desc_amount")
      : t("join_step_confirm_desc");

  const footer =
    step === 0 ? (
      <LoadingButton
        variant="contained"
        size="large"
        fullWidth
        loading={isFetching}
        disabled={!codeValid}
        onClick={handleLookup}
      >
        {t("next")}
      </LoadingButton>
    ) : step === 1 ? (
      <div className="flex gap-2">
        <Button variant="outlined" size="large" onClick={() => setStep(0)} fullWidth>
          {t("back")}
        </Button>
        <Button
          variant="contained"
          size="large"
          onClick={() => setStep(2)}
          disabled={!shareValid}
          fullWidth
        >
          {t("next")}
        </Button>
      </div>
    ) : (
      <div className="flex gap-2">
        <Button variant="outlined" size="large" onClick={() => setStep(1)} fullWidth>
          {t("back")}
        </Button>
        <LoadingButton variant="contained" size="large" loading={joining} onClick={handleJoin} fullWidth>
          {t("hatim_join_code_submit")}
        </LoadingButton>
      </div>
    );

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width="sm"
      title={t("hatim_join_code_modal_title")}
      subtitle={t("hatim_join_drawer_subtitle")}
      titleIcon={<HatimIcon />}
      footer={footer}
    >
      {/* Panel sayfa kabının dışında çizilir: koyu tema eşlemesi burada da geçerli */}
      <div className="tk-dark p-6 flex flex-col gap-6">
        <StepHeader step={step} title={stepTitle} description={stepDesc} t={t} />

        {step === 0 && (
          <div className="flex flex-col gap-3">
            <CodeBoxes value={code} onChange={setCode} disabled={isFetching} />
            {isError ? (
              <p className="text-base text-terakkiError">{t("hatim_join_code_lookup_error")}</p>
            ) : (
              <p className="text-sm text-terakkiNeutral">{t("hatim_join_code_help")}</p>
            )}
            {isFetching && (
              <div className="flex items-center gap-2 text-base text-terakkiNeutral">
                <CircularProgress size={16} />
                {t("loading")}
              </div>
            )}
          </div>
        )}

        {step === 1 && info && (
          <div className="flex flex-col gap-5">
            <div className="rounded-2xl bg-terakkiBrandCloud p-5">
              <div className="text-lg font-SemiBold text-terakkiPrimaryDark">{info.name}</div>
              <div className="text-base text-terakkiNeutral mt-0.5">{info.type}</div>
            </div>

            {isRange ? (
              <div>
                <p className="text-base font-SemiBold text-terakkiInk mb-3">{t("hatim_join_pick_blocks")}</p>
                {availableBlocks.length === 0 ? (
                  <p className="text-base text-terakkiNeutral">{t("hatim_join_no_blocks")}</p>
                ) : (
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                    {availableBlocks.map((block) => {
                      const active = blocks.some((b) => b.juz === block.juz);
                      return (
                        <button
                          key={block.juz}
                          type="button"
                          aria-pressed={active}
                          onClick={() => toggleBlock(block)}
                          className={[
                            "px-3 py-3 rounded-xl text-base font-SemiBold border-2 transition-colors",
                            active
                              ? "bg-terakkiPrimaryDark text-white border-terakkiPrimaryDark"
                              : "bg-terakkiSurfaceAlt text-terakkiInk border-terakkiBorder hover:border-terakkiPrimary",
                          ].join(" ")}
                        >
                          {block.startPage}–{block.endPage}
                        </button>
                      );
                    })}
                  </div>
                )}
                {blocks.length > 0 && (
                  <p className="text-base text-terakkiPrimaryDark font-SemiBold mt-3">
                    {t("hatim_join_selected_total")}: {sumPages(blocks)} {t("hatim_pages_unit")}
                  </p>
                )}
              </div>
            ) : (
              <div>
                <p className="text-base font-SemiBold text-terakkiInk mb-3">
                  {t("hatim_join_code_amount_label")}
                </p>
                <GoalValueControl
                  value={Number(amount)}
                  min={1}
                  max={maxCount > 0 ? maxCount : 9999}
                  onChange={setAmount}
                />
                {maxCount > 0 && (
                  <p className="text-sm text-terakkiNeutral mt-2">
                    {t("hatim_join_code_preview_max").replace("{n}", maxCount)}
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {step === 2 && info && (
          <div className="rounded-2xl border border-terakkiBorder p-5">
            <InfoLine label={t("hatim_join_summary_name")} value={info.name} />
            <InfoLine label={t("hatim_join_summary_type")} value={info.type} />
            <InfoLine label={t("hatim_join_code_label")} value={code} />
            <InfoLine
              label={t("hatim_join_summary_share")}
              value={
                isRange
                  ? `${sumPages(blocks)} ${t("hatim_pages_unit")}`
                  : `${amount} ${t("hatim_card_assigned_amount")}`
              }
            />
            {isRange && blocks.length > 0 && (
              <InfoLine
                label={t("hatim_join_summary_ranges")}
                value={blocks
                  .slice()
                  .sort((a, b) => a.startPage - b.startPage)
                  .map((b) => `${b.startPage}–${b.endPage}`)
                  .join(", ")}
              />
            )}
          </div>
        )}
      </div>
    </Drawer>
  );
}
